import {
  RawCheckoutResponse,
  RawAdditionalBillingInfoExtension,
  RawAvailablePaymentMethodsExtension,
  RawLegalAgreementsExtension,
  RawMailingConsentExtension,
} from "../../../../types/api/wcStoreApi/wcStoreApiCheckoutEndpointsTypes";
import { RawDeliveryEstimationExtension } from "../../../../types/api/wcStoreApi/wcStoreApiProductsEndpointTypes";
import { transformAddress as transformBillingAddress } from "../../cart/helpers/cartApiHelpers/cartDataTransformationHelpers/transformAddress";
import { transformAddress as transformShippingAddress } from "../../cart/helpers/cartApiHelpers/cartDataTransformationHelpers/transformAddress";
import { transformCart } from "../../cart/helpers/cartApiHelpers/transformCart";
import { store } from "../../../../app/store";
import { selectLanguageInfo } from "../../../../common/features/language/languageSelectors";
import {
  TransformedCheckoutData,
  TransformedLegalAgreements,
} from "../types/checkoutApiTypes";
import { TransformedCart } from "../../cart/types/cartApiTypes";

export const transformCheckoutResponse = (
  response: RawCheckoutResponse,
): TransformedCheckoutData => {
  const languageInfo = selectLanguageInfo(store.getState());

  // 1. Extract Additional Billing Info (TCKN/VKN)
  const additionalBillingInfo = response.extensions?.additional_billing_info as
    | RawAdditionalBillingInfoExtension
    | undefined;

  const billingExtras = additionalBillingInfo
    ? {
        customerType: additionalBillingInfo.customer_type,
        tckn: additionalBillingInfo.tckn,
        vkn: additionalBillingInfo.vkn,
      }
    : undefined;

  // 2. Extract Available Payment Methods
  const availablePaymentMethods = (
    response.extensions?.available_payment_methods as
      | RawAvailablePaymentMethodsExtension
      | undefined
  )?.methods;

  // 3. Extract & Transform Legal Agreements
  const rawLegalAgreements = response.extensions?.legal_agreements as
    | RawLegalAgreementsExtension
    | undefined;

  let legalAgreements: TransformedLegalAgreements | undefined;

  if (rawLegalAgreements) {
    legalAgreements = {
      agreed: rawLegalAgreements.agreed,
      templates: {
        preliminaryInfo: rawLegalAgreements.templates.preliminary_info,
        distanceSalesAgreement:
          rawLegalAgreements.templates.distance_sales_agreement,
      },
      sellerInfo: {
        name: rawLegalAgreements.seller_info.name,
        address: rawLegalAgreements.seller_info.address,
        phone: rawLegalAgreements.seller_info.phone,
        email: rawLegalAgreements.seller_info.email,
        mersis: rawLegalAgreements.seller_info.mersis,
      },
    };
  }

  // 4. Extract Delivery Estimation
  const deliveryRaw = response.extensions?.delivery_estimation as
    | RawDeliveryEstimationExtension
    | undefined;
  const deliveryEstimation = deliveryRaw
    ? {
        minDate: deliveryRaw.min_date,
        maxDate: deliveryRaw.max_date,
        minDays: deliveryRaw.min_days,
        maxDays: deliveryRaw.max_days,
        displayString: deliveryRaw.display_string,
      }
    : undefined;

  // 5. Extract Mailing Consent
  const rawMailingConsent = response.extensions?.mailing_consent as
    | RawMailingConsentExtension
    | undefined;

  const mailingConsent = rawMailingConsent
    ? { subscribed: rawMailingConsent.subscribed }
    : undefined;

  // --- EXTRACT APPOINTMENT ---
  const rawAppointment = response.extensions?.appointment as any;
  let needsAppointment = false;
  if (rawAppointment) {
    needsAppointment =
      typeof rawAppointment === "object"
        ? !!rawAppointment.needs_appointment
        : !!rawAppointment;
  }
  const appointment = { needsAppointment };

  // 6. Transform Addresses
  const transformedBillingAddress = response.billing_address
    ? transformBillingAddress(response.billing_address, billingExtras)
    : ({} as TransformedCart["billingAddress"]);

  const transformedShippingAddress = response.shipping_address
    ? transformShippingAddress(response.shipping_address)
    : ({} as TransformedCart["shippingAddress"]);

  // 7. Transform Optional Cart Update
  let updatedCart: TransformedCart | undefined;
  if (response.__experimentalCart) {
    updatedCart = transformCart(response.__experimentalCart, languageInfo);
  }

  // 8. Transform Payment Result
  let transformedPaymentResult: TransformedCheckoutData["paymentResult"] = null;

  if (response.payment_result) {
    const rawDetails = response.payment_result.payment_details || [];
    const detailsMap: Record<string, any> = {};

    if (Array.isArray(rawDetails)) {
      rawDetails.forEach((item: { key: string; value: any }) => {
        detailsMap[item.key] = item.value;
      });
    }

    transformedPaymentResult = {
      payment_status: response.payment_result.payment_status,
      paymentDetails: detailsMap,
      redirectUrl: response.payment_result.redirect_url,
    };
  }

  return {
    orderId: response.order_id,
    status: response.status,
    orderKey: response.order_key,
    customerNote: response.customer_note,
    customerId: response.customer_id,
    billingAddress: transformedBillingAddress,
    shippingAddress: transformedShippingAddress,
    paymentMethod: response.payment_method,
    paymentResult: transformedPaymentResult,
    updatedCart: updatedCart,
    additionalFields: response.additional_fields || {},
    extensions: response.extensions || {},
    availablePaymentMethods: availablePaymentMethods || [],
    legalAgreements: legalAgreements,
    deliveryEstimation: deliveryEstimation,
    mailingConsent: mailingConsent,
    appointment: appointment,
  };
};
